import { useState } from "react";
import useSWR from "swr";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatIDR, periodLabel } from "@/lib/format";
import { FileDown, FileText, FileSpreadsheet } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

const fetcher = (url) => http.get(url).then((r) => r.data);

export default function Riwayat() {
  const { data: history } = useSWR("/history", fetcher);
  const [busy, setBusy] = useState(null);

  const download = async (kind, period = "all") => {
    setBusy(kind + period);
    try {
      const res = await fetch(`${API}/export/${kind}?period=${period}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Gagal mengunduh");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = kind === "excel" ? "xlsx" : kind === "csv" ? "csv" : "pdf";
      a.download = `asset-tracker-${period}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Berkas ${ext.toUpperCase()} diunduh`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const rows = history || [];
  const withDelta = rows.map((r, i) => {
    const next = rows[i + 1];
    const delta = next ? r.total - next.total : 0;
    const pct = next && next.total > 0 ? (delta / next.total) * 100 : null;
    return { ...r, delta, pct };
  });

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Laporan</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Riwayat & Ekspor</h1>
          <p className="mt-1 text-slate-600">Ringkasan total per periode dan unduh laporan lengkap.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2 border-slate-200" onClick={() => download("csv", "all")} disabled={busy === "csvall"} data-testid="export-csv-all">
            <FileDown className="w-4 h-4" /> CSV
          </Button>
          <Button variant="outline" className="gap-2 border-slate-200" onClick={() => download("excel", "all")} disabled={busy === "excelall"} data-testid="export-excel-all">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
          <Button variant="outline" className="gap-2 border-slate-200" onClick={() => download("pdf", "all")} disabled={busy === "pdfall"} data-testid="export-pdf-all">
            <FileText className="w-4 h-4" /> PDF
          </Button>
        </div>
      </header>

      <Card className="overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-slate-100 text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 bg-slate-50/60">
          <div className="col-span-4">Periode</div>
          <div className="col-span-3 text-right">Total</div>
          <div className="col-span-3 text-right">Δ vs bulan lalu</div>
          <div className="col-span-2 text-right">Aksi</div>
        </div>
        <div className="divide-y divide-slate-100">
          {rows.length === 0 && (
            <div className="px-6 py-16 text-center text-slate-500">
              Belum ada riwayat. Mulai catat aset pertama Anda di halaman <span className="font-medium text-slate-700">Input Bulanan</span>.
            </div>
          )}
          {withDelta.map((r) => (
            <div key={r.period} className="grid grid-cols-12 px-6 py-3 items-center" data-testid={`history-row-${r.period}`}>
              <div className="col-span-4">
                <div className="font-medium text-slate-800">{periodLabel(r.period)}</div>
                <div className="text-xs text-slate-500 tabular">{r.items} item tercatat</div>
              </div>
              <div className="col-span-3 text-right font-medium text-slate-900 tabular">{formatIDR(r.total)}</div>
              <div className={`col-span-3 text-right text-sm tabular ${r.delta > 0 ? "text-emerald-600" : r.delta < 0 ? "text-rose-600" : "text-slate-500"}`}>
                {r.delta === 0 ? "—" : (r.delta > 0 ? "+" : "") + formatIDR(r.delta)}
                {r.pct !== null && r.delta !== 0 && (
                  <span className="ml-1 text-xs text-slate-500">({(r.pct > 0 ? "+" : "") + r.pct.toFixed(1)}%)</span>
                )}
              </div>
              <div className="col-span-2 flex justify-end gap-1">
                <Button variant="ghost" size="sm" onClick={() => download("pdf", r.period)} data-testid={`export-pdf-${r.period}`}>
                  <FileText className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => download("excel", r.period)} data-testid={`export-excel-${r.period}`}>
                  <FileSpreadsheet className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
